import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Shield, Calendar, Mail, Building, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockData } from './mock';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-12 sm:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg border border-gray-200 hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ArrowLeft size={16} />
            <span className="font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-blue-200/50 backdrop-blur-sm">
            <Shield size={16} className="sm:w-4 sm:h-4 text-blue-600" />
            <Badge className="bg-transparent text-blue-700 px-0 py-0 text-xs sm:text-sm font-semibold">
              Legal Document
            </Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Privacy Policy
          </h1>
          <div className="flex items-center justify-center gap-4 text-sm sm:text-base text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="sm:w-4 sm:h-4" />
              <span>Last updated: October 02, 2025</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-4 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Building size={16} className="sm:w-5 sm:h-5 text-white" />
                </div>
                <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  Traffic Service International LLC
                </CardTitle>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm">
                Building No: 915, office No: 579, CBD (Abu Dhabi bank) Al Falaj street, Muscat, Sultanate of Oman, Postal Code: 111
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-6 sm:space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Introduction</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
                  This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
              </section>

              {/* Interpretation and Definitions */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Interpretation and Definitions</h2>
                
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Interpretation</h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
                  The words whose initial letters are capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
                </p>

                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Definitions</h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">For the purposes of this Privacy Policy:</p>
                
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Account</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">means a unique account created for You to access our Service or parts of our Service.</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Company</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">(referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Traffic Service International LLC, Building No: 915, office No: 579, CBD (Abu Dhabi bank) Al Falaj street, Muscat, Sultanate of Oman.</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Cookies</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Country</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">refers to: Oman</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Personal Data</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">is any information that relates to an identified or identifiable individual.</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Service</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">refers to the Website.</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">Website</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">refers to Traffic Service International LLC, accessible from www.tsioman.com/</p>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900 text-sm sm:text-base">You</strong>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">mean the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</p>
                  </div>
                </div>
              </section>

              {/* Collecting and Using Personal Data */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Collecting and Using Your Personal Data</h2>
                
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Types of Data Collected</h3>
                
                <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">Personal Data</h4>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm sm:text-base ml-4 mb-4">
                  <li>Email address</li>
                  <li>First name and last name</li>
                  <li>Phone number</li>
                  <li>Address, State, Province, ZIP/Postal code, City</li>
                </ul>

                <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-2">Usage Data</h4>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  Usage Data is collected automatically when using the Service. Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                </p>
              </section>

              {/* Use of Personal Data */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Use of Your Personal Data</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">The Company may use Personal Data for the following purposes:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base ml-4">
                  <li>To provide and maintain our Service, including to monitor the usage of our Service.</li>
                  <li>To manage Your Account: to manage Your registration as a user of the Service.</li>
                  <li>For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased.</li>
                  <li>To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication.</li>
                  <li>To provide You with news, special offers, and general information about other goods, services and events.</li>
                  <li>To manage Your requests: To attend and manage Your requests to Us.</li>
                  <li>For other purposes: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns.</li>
                </ul>
              </section>

              {/* Data Sharing */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Sharing Your Personal Information</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">We may share Your personal information in the following situations:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base ml-4">
                  <li>With Service Providers: We may share Your personal information with Service Providers to monitor and analyze the use of our Service.</li>
                  <li>For business transfers: We may share or transfer Your personal information in connection with any merger, sale of Company assets, financing, or acquisition.</li>
                  <li>With Affiliates: We may share Your information with Our affiliates.</li>
                  <li>With business partners: We may share Your information with Our business partners to offer You certain products, services or promotions.</li>
                  <li>With Your consent: We may disclose Your personal information for any other purpose with Your consent.</li>
                </ul>
              </section>

              {/* Data Security */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Security of Your Personal Data</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially reasonable means to protect Your Personal Data, We cannot guarantee its absolute security.
                </p>
              </section>

              {/* Children's Privacy */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Children's Privacy</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us.
                </p>
              </section>

              {/* Changes to Privacy Policy */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Changes to this Privacy Policy</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Contact Us</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  If you have any questions about this Privacy Policy, You can contact us:
                </p>
                <div className="flex items-center gap-3 p-4 bg-blue-50/50 rounded-lg">
                  <Mail size={16} className="sm:w-5 sm:h-5 text-blue-600" />
                  <span className="text-blue-700 font-medium text-sm sm:text-base">{mockData.contact.email}</span>
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
